import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "redux/hooks";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import { Typography } from "components";
import { Row, Col } from "components/BaseComponents/Flex";
import {
  StyledEmpPanelItemCircle,
  StyledEmpPanelItemWrapper,
  StyledItemMenuWrapper,
} from "./EmployeePanel.styles";
import Image from "components/BaseComponents/Image";
import {
  formattingUserName,
  initialsFromName,
  maxName,
  maxUserName,
} from "utils/textfield.utils";

import AgentActiveInactive from "../common/AgentActiveInactive";
import AgentRemove from "../common/AgentRemove";
import AgentEdit from "../common/AgentEdit";

interface EmployeePanelItemProps {
  name?: string;
  phoneNumber?: string;
  status?: string;
  profileImage?: string;
  isAdmin: boolean;
}

const EmployeePanelItem: FC<EmployeePanelItemProps> = ({
  name,
  phoneNumber,
  status,
  profileImage,
  isAdmin,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const dispatch = useAppDispatch();

  const EmployeeDetail = (
    name: string,
    phoneNumber: string,
    status: string
  ) => {
    navigate(`/employee-detail/${name}/${phoneNumber}/${status}`);
  };

  return (
    <StyledEmpPanelItemWrapper>
      <Row
        backgroundColor={DIGIPOSTHEME.colors.transparent}
        noMargin
        onClick={() =>
          !isAdmin ? EmployeeDetail(name!, phoneNumber!, status!) : null
        }
        isCursor={!isAdmin ? true : false}
        width='auto'
      >
        <Col>
          <StyledEmpPanelItemCircle>
            {profileImage ? (
              <Image
                alt='Profile Image'
                srcSet={
                  profileImage
                    ? `data:image/png;base64, ${profileImage}`
                    : undefined
                }
                height='40px'
                width='40px'
                borderRadius='50%'
                border='1px solid #f5f4f6'
                margin='15px 0'
                padding='0'
              />
            ) : (
              <Typography
                size='12px'
                color={DIGIPOSTHEME.colors.headerRed}
                width=''
                weight='1000'
              >
                {maxName(formattingUserName(initialsFromName(name)))}
              </Typography>
            )}
          </StyledEmpPanelItemCircle>
        </Col>
        <Col minWidth={isMobile ? "max-content" : "none"}>
          <Typography
            width={isMobile ? "" : "400px"}
            margin={isMobile ? "0 5px" : "0 15px"}
            size='18px'
            hoverColor={
              !isAdmin
                ? DIGIPOSTHEME.colors.headerRed
                : DIGIPOSTHEME.colors.black
            }
            display={isMobile ? "flex" : "contents"}
          >
            {!isAdmin
              ? isMobile
                ? maxUserName(name)
                : name
              : isMobile
              ? maxUserName(name, 22)
              : name}
          </Typography>
          <Typography
            width='300px'
            margin={isMobile ? "0 5px" : "0 15px"}
            size='10px'
            color={DIGIPOSTHEME.colors.secondaryGraySecondary}
            display={isMobile ? "flex" : "contents"}
          >
            {phoneNumber}
          </Typography>
        </Col>
      </Row>
      {!isAdmin && (
        <>
          <StyledItemMenuWrapper>
            <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
              <Col justifyContent='flex-end'>
                <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
                  <AgentEdit
                    isDetail={false}
                    phoneNumber={phoneNumber!}
                    name={name!}
                    t={t}
                  />

                  <AgentActiveInactive
                    dispatch={dispatch}
                    phoneNumber={phoneNumber!}
                    t={t}
                    status={status!}
                    isDetail={false}
                  />

                  <AgentRemove
                    t={t}
                    phoneNumber={phoneNumber!}
                    name={name!}
                    onClick={(event: any) => event}
                    isDetail={false}
                  />
                </Row>
              </Col>
            </Row>
          </StyledItemMenuWrapper>
        </>
      )}
    </StyledEmpPanelItemWrapper>
  );
};

export default EmployeePanelItem;
